/**
 * AppConfig Component
 *
 * Admin panel page for managing global app configuration.
 * Currently supports:
 * - In-App Review settings (enable/disable, timing, triggers)
 *
 * Future:
 * - Maintenance mode
 * - Feature flags
 * - Other global settings
 */

import React, { useState, useEffect, useCallback } from "react";
import myAxios from "../AxiosUrl";
import "./AppConfig.css";

function AppConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // In-App Review Settings
  const [reviewEnabled, setReviewEnabled] = useState(true);
  const [minLaunches, setMinLaunches] = useState(3);
  const [daysBetweenPrompts, setDaysBetweenPrompts] = useState(30);
  const [minDaysSinceInstall, setMinDaysSinceInstall] = useState(3);
  const [showAfterProfileComplete, setShowAfterProfileComplete] = useState(true);
  const [showAfterMatch, setShowAfterMatch] = useState(true);
  const [showAfterFirstMessage, setShowAfterFirstMessage] = useState(false);
  const [showAfterPurchase, setShowAfterPurchase] = useState(true);

  // Face Verification Settings
  const [allowVideoSkip, setAllowVideoSkip] = useState(false);

  // App Payments (App Store Review Guideline 3.1.1, Google Play Payments policy).
  // Store builds from 1.0.79 force "website" on their own; these switches only
  // matter for a direct-download Android build. websiteUrl/showLink are kept
  // only for iOS builds ≤ 77 and are saved back unchanged.
  const [iosPayMode, setIosPayMode] = useState("bkash");
  const [iosWebsiteUrl, setIosWebsiteUrl] = useState("https://app.bibahobangla.com");
  const [iosShowLink, setIosShowLink] = useState(false);
  const [iosMessageEn, setIosMessageEn] = useState("");
  const [iosMessageBn, setIosMessageBn] = useState("");
  const [androidPayMode, setAndroidPayMode] = useState("website");
  const [androidMessageEn, setAndroidMessageEn] = useState("");
  const [androidMessageBn, setAndroidMessageBn] = useState("");

  // Fetch current config on mount
  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await myAxios.get("/app-config");

      if (response.data?.success && response.data?.data?.inAppReview) {
        const config = response.data.data.inAppReview;
        setReviewEnabled(config.enabled ?? true);
        setMinLaunches(config.minLaunches ?? 3);
        setDaysBetweenPrompts(config.daysBetweenPrompts ?? 30);
        setMinDaysSinceInstall(config.minDaysSinceInstall ?? 3);
        setShowAfterProfileComplete(config.showAfterProfileComplete ?? true);
        setShowAfterMatch(config.showAfterMatch ?? true);
        setShowAfterFirstMessage(config.showAfterFirstMessage ?? false);
        setShowAfterPurchase(config.showAfterPurchase ?? true);
      }

      if (response.data?.success) {
        setAllowVideoSkip(
          response.data?.data?.faceVerification?.allowSkip ?? false
        );
        const ios = response.data?.data?.iosPayments || {};
        setIosPayMode(ios.mode === "website" ? "website" : "bkash");
        setIosWebsiteUrl(ios.websiteUrl || "https://app.bibahobangla.com");
        setIosShowLink(ios.showLink === true);
        setIosMessageEn(ios.messageEn || "");
        setIosMessageBn(ios.messageBn || "");
        const android = response.data?.data?.androidPayments || {};
        setAndroidPayMode(android.mode === "bkash" ? "bkash" : "website");
        setAndroidMessageEn(android.messageEn || "");
        setAndroidMessageBn(android.messageBn || "");
      }
    } catch (err) {
      console.error("Error fetching app config:", err);
      setError("Failed to load app configuration");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Save config
  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const uid = localStorage.getItem("id");
      const response = await myAxios.put(`/app-config/${uid}`, {
        inAppReview: {
          enabled: reviewEnabled,
          minLaunches: parseInt(minLaunches, 10),
          daysBetweenPrompts: parseInt(daysBetweenPrompts, 10),
          minDaysSinceInstall: parseInt(minDaysSinceInstall, 10),
          showAfterProfileComplete,
          showAfterMatch,
          showAfterFirstMessage,
          showAfterPurchase,
        },
        faceVerification: {
          allowSkip: allowVideoSkip,
        },
        iosPayments: {
          mode: iosPayMode,
          websiteUrl: iosWebsiteUrl.trim(),
          showLink: iosShowLink,
          messageEn: iosMessageEn,
          messageBn: iosMessageBn,
        },
        androidPayments: {
          mode: androidPayMode,
          messageEn: androidMessageEn,
          messageBn: androidMessageBn,
        },
      });

      if (response.data?.success) {
        setSuccess("Configuration saved successfully!");
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(response.data?.error || "Failed to save configuration");
      }
    } catch (err) {
      console.error("Error saving app config:", err);
      setError(err.response?.data?.error || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="app-config-container">
        <div className="app-config-loading">
          <div className="spinner"></div>
          <p>Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-config-container">
      <div className="app-config-header">
        <h1 className="app-config-title">App Configuration</h1>
        <p className="app-config-subtitle">
          Manage global app settings for mobile applications
        </p>
      </div>

      {error && (
        <div className="app-config-alert error">
          <span className="material-icons">error</span>
          {error}
        </div>
      )}

      {success && (
        <div className="app-config-alert success">
          <span className="material-icons">check_circle</span>
          {success}
        </div>
      )}

      <div className="app-config-content">
        {/* In-App Review Section */}
        <div className="app-config-section">
          <div className="app-config-section-header">
            <span className="material-icons app-config-section-icon">
              star_rate
            </span>
            <div>
              <h2 className="app-config-section-title">In-App Review</h2>
              <p className="app-config-section-description">
                Configure when and how users are prompted to rate the app
              </p>
            </div>
          </div>

          <div className="app-config-section-content">
            {/* Master Toggle */}
            <div className="app-config-toggle-item highlight">
              <div>
                <label className="app-config-toggle-label">
                  Enable Review Prompts
                </label>
                <p className="app-config-toggle-description">
                  Master switch to enable or disable all in-app review prompts
                </p>
              </div>
              <label className="app-config-toggle-switch">
                <input
                  type="checkbox"
                  checked={reviewEnabled}
                  onChange={(e) => setReviewEnabled(e.target.checked)}
                />
                <span className="app-config-slider"></span>
              </label>
            </div>

            {/* Timing Settings */}
            <div className="app-config-subsection">
              <h3 className="app-config-subsection-title">Timing Settings</h3>

              <div className="app-config-form-row">
                <div className="app-config-form-group">
                  <label className="app-config-label">
                    Minimum App Launches
                  </label>
                  <input
                    type="number"
                    className="app-config-input"
                    value={minLaunches}
                    onChange={(e) => setMinLaunches(e.target.value)}
                    min="1"
                    max="100"
                    disabled={!reviewEnabled}
                  />
                  <p className="app-config-input-help">
                    User must launch the app at least this many times before
                    seeing a review prompt
                  </p>
                </div>

                <div className="app-config-form-group">
                  <label className="app-config-label">
                    Days Between Prompts
                  </label>
                  <input
                    type="number"
                    className="app-config-input"
                    value={daysBetweenPrompts}
                    onChange={(e) => setDaysBetweenPrompts(e.target.value)}
                    min="1"
                    max="365"
                    disabled={!reviewEnabled}
                  />
                  <p className="app-config-input-help">
                    Minimum days to wait before showing another prompt
                  </p>
                </div>

                <div className="app-config-form-group">
                  <label className="app-config-label">
                    Min Days Since Install
                  </label>
                  <input
                    type="number"
                    className="app-config-input"
                    value={minDaysSinceInstall}
                    onChange={(e) => setMinDaysSinceInstall(e.target.value)}
                    min="0"
                    max="90"
                    disabled={!reviewEnabled}
                  />
                  <p className="app-config-input-help">
                    Wait this many days after install before first prompt
                  </p>
                </div>
              </div>
            </div>

            {/* Trigger Points */}
            <div className="app-config-subsection">
              <h3 className="app-config-subsection-title">Trigger Points</h3>
              <p className="app-config-subsection-description">
                Choose when to show review prompts (at positive moments)
              </p>

              <div className="app-config-toggle-grid">
                <div className="app-config-toggle-item">
                  <div>
                    <label className="app-config-toggle-label">
                      After Profile Complete
                    </label>
                    <p className="app-config-toggle-description">
                      Show prompt when user completes their profile setup
                    </p>
                  </div>
                  <label className="app-config-toggle-switch">
                    <input
                      type="checkbox"
                      checked={showAfterProfileComplete}
                      onChange={(e) =>
                        setShowAfterProfileComplete(e.target.checked)
                      }
                      disabled={!reviewEnabled}
                    />
                    <span className="app-config-slider"></span>
                  </label>
                </div>

                <div className="app-config-toggle-item">
                  <div>
                    <label className="app-config-toggle-label">
                      After Match
                    </label>
                    <p className="app-config-toggle-description">
                      Show prompt after user gets a successful match
                    </p>
                  </div>
                  <label className="app-config-toggle-switch">
                    <input
                      type="checkbox"
                      checked={showAfterMatch}
                      onChange={(e) => setShowAfterMatch(e.target.checked)}
                      disabled={!reviewEnabled}
                    />
                    <span className="app-config-slider"></span>
                  </label>
                </div>

                <div className="app-config-toggle-item">
                  <div>
                    <label className="app-config-toggle-label">
                      After First Message
                    </label>
                    <p className="app-config-toggle-description">
                      Show prompt after user sends their first message
                    </p>
                  </div>
                  <label className="app-config-toggle-switch">
                    <input
                      type="checkbox"
                      checked={showAfterFirstMessage}
                      onChange={(e) =>
                        setShowAfterFirstMessage(e.target.checked)
                      }
                      disabled={!reviewEnabled}
                    />
                    <span className="app-config-slider"></span>
                  </label>
                </div>

                <div className="app-config-toggle-item">
                  <div>
                    <label className="app-config-toggle-label">
                      After Purchase
                    </label>
                    <p className="app-config-toggle-description">
                      Show prompt after a successful subscription purchase
                    </p>
                  </div>
                  <label className="app-config-toggle-switch">
                    <input
                      type="checkbox"
                      checked={showAfterPurchase}
                      onChange={(e) => setShowAfterPurchase(e.target.checked)}
                      disabled={!reviewEnabled}
                    />
                    <span className="app-config-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Face Verification Section */}
        <div className="app-config-section">
          <div className="app-config-section-header">
            <span className="material-icons app-config-section-icon">
              verified_user
            </span>
            <div>
              <h2 className="app-config-section-title">Face Verification</h2>
              <p className="app-config-section-description">
                Control whether users can skip face video verification during signup
              </p>
            </div>
          </div>

          <div className="app-config-section-content">
            <div className="app-config-toggle-item highlight">
              <div>
                <label className="app-config-toggle-label">
                  Allow users to skip face video verification
                </label>
                <p className="app-config-toggle-description">
                  When ON, users can skip the video step during signup and will be
                  prompted on the dashboard. Interest, shortlist, accept and chat
                  remain locked until they verify. Applies to both the web app and
                  the mobile app.
                </p>
              </div>
              <label className="app-config-toggle-switch">
                <input
                  type="checkbox"
                  checked={allowVideoSkip}
                  onChange={(e) => setAllowVideoSkip(e.target.checked)}
                />
                <span className="app-config-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* App Payments Section (iOS + Android) */}
        <div className="app-config-section">
          <div className="app-config-section-header">
            <span className="material-icons app-config-section-icon">
              payments
            </span>
            <div>
              <h2 className="app-config-section-title">App Payments</h2>
              <p className="app-config-section-description">
                Apple (App Store guideline 3.1.1) and Google Play (Payments
                policy, notice on version 77) only allow their own billing for
                paid features, so a bKash checkout inside an app gets it
                rejected or removed. In "Pay from the website" mode the app
                hides bKash in the package, subscription and offer screens and
                in wallet top-up, and shows the short notice below; paying from
                an existing wallet balance keeps working. Members buy on
                bibahobangla.com/account with the same number. Store builds from
                1.0.79 are always in website mode; the switches below only
                matter for a direct-download Android build. The web app is not
                affected.
              </p>
            </div>
          </div>

          <div className="app-config-section-content">
            <h3 className="app-config-label" style={{ marginBottom: 8 }}>iPhone app</h3>
            <div className="app-config-form-row">
              <div className="app-config-form-group">
                <label className="app-config-label">Payment mode on iOS</label>
                <select
                  className="app-config-input"
                  value={iosPayMode}
                  onChange={(e) => setIosPayMode(e.target.value)}
                >
                  <option value="bkash">bKash inside the app (direct builds only)</option>
                  <option value="website">Pay from the website (bKash hidden)</option>
                </select>
                <p className="app-config-input-help">
                  iPhone builds up to 1.0.77 also show the web address
                  app.bibahobangla.com in their notice; builds from 1.0.79 show
                  the notice text only, with no address or link.
                </p>
              </div>
            </div>

            <div className="app-config-form-row">
              <div className="app-config-form-group">
                <label className="app-config-label">iOS notice text (English)</label>
                <textarea
                  className="app-config-input"
                  rows={3}
                  value={iosMessageEn}
                  onChange={(e) => setIosMessageEn(e.target.value)}
                  placeholder="Packages and wallet top-ups can't be bought in this app."
                  disabled={iosPayMode !== "website"}
                />
              </div>
              <div className="app-config-form-group">
                <label className="app-config-label">iOS notice text (Bangla)</label>
                <textarea
                  className="app-config-input"
                  rows={3}
                  value={iosMessageBn}
                  onChange={(e) => setIosMessageBn(e.target.value)}
                  placeholder="এই অ্যাপে প্যাকেজ বা ওয়ালেট টপ-আপ কেনা যায় না।"
                  disabled={iosPayMode !== "website"}
                />
              </div>
            </div>

            <h3 className="app-config-label" style={{ marginTop: 24, marginBottom: 8 }}>Android app</h3>
            <div className="app-config-form-row">
              <div className="app-config-form-group">
                <label className="app-config-label">Payment mode on Android</label>
                <select
                  className="app-config-input"
                  value={androidPayMode}
                  onChange={(e) => setAndroidPayMode(e.target.value)}
                >
                  <option value="website">Pay from the website (bKash hidden)</option>
                  <option value="bkash">bKash inside the app (direct-download build only)</option>
                </select>
                <p className="app-config-input-help">
                  Play Store builds ignore "bKash inside the app". Android builds
                  up to 1.0.77 ignore this setting and always show bKash, which is
                  why they must be force-updated.
                </p>
              </div>
            </div>

            <div className="app-config-form-row">
              <div className="app-config-form-group">
                <label className="app-config-label">Android notice text (English)</label>
                <textarea
                  className="app-config-input"
                  rows={3}
                  value={androidMessageEn}
                  onChange={(e) => setAndroidMessageEn(e.target.value)}
                  placeholder="Packages and wallet top-ups can't be bought in this app."
                  disabled={androidPayMode !== "website"}
                />
              </div>
              <div className="app-config-form-group">
                <label className="app-config-label">Android notice text (Bangla)</label>
                <textarea
                  className="app-config-input"
                  rows={3}
                  value={androidMessageBn}
                  onChange={(e) => setAndroidMessageBn(e.target.value)}
                  placeholder="এই অ্যাপে প্যাকেজ বা ওয়ালেট টপ-আপ কেনা যায় না।"
                  disabled={androidPayMode !== "website"}
                />
              </div>
            </div>
            <p className="app-config-input-help">
              Do not write a web address or "buy on our website" in the notices:
              both stores treat that as leading users to another payment method.
            </p>
          </div>
        </div>

        {/* Info Box */}
        <div className="app-config-info-box">
          <span className="material-icons">info</span>
          <div>
            <strong>How In-App Review Works</strong>
            <p>
              The app checks all conditions before showing a review prompt.
              Users who have already reviewed won't be prompted again.
              The native iOS/Android review dialog is shown, keeping users in the app.
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="app-config-actions">
          <button
            className="app-config-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-small"></span>
                Saving...
              </>
            ) : (
              <>
                <span className="material-icons">save</span>
                Save Configuration
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AppConfig;
